/**
 * Invoicing header — QuickBooks (24 #6): a status chip ("QuickBooks connected · synced 2 h ago" /
 * "not connected" / "not set up") from GET /quickbooks/status, and "Sync N to QuickBooks", which sends every
 * raised invoice without a QuickBooks number in one request (POST /quickbooks/sync-pending).
 * The bulk sync is switched off on the server unless IT sets QUICKBOOKS_BULK_SYNC_ENABLED (see
 * docs/INTEGRATIONS_SETUP.md); until then the button stays disabled and says why.
 */
import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { formatNumber, formatTimeAgo } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { notify } from '@/components/ui/notify';
import { plural } from './SfUi';

export function useQuickBooksStatus(enabled = true) {
  const [status, setStatus] = useState(null);
  const load = useCallback(async () => {
    try {
      const res = await api.get('/quickbooks/status');
      setStatus(res.data || {});
    } catch {
      setStatus({ unavailable: true });
    }
  }, []);
  useEffect(() => { if (enabled) load(); }, [enabled, load]);
  return status;
}

/** 'connected' | 'not_connected' | 'not_configured' | 'unknown' */
export function quickBooksState(status) {
  if (!status || status.unavailable) return 'unknown';
  if (status.configured === false) return 'not_configured';
  if (!status.connected) return 'not_connected';
  return 'connected';
}

/** Why "Sync N to QuickBooks" cannot run right now ('' = it can). */
export function syncBlockedReason(status, pendingCount) {
  const state = quickBooksState(status);
  if (state === 'unknown') return 'Could not read the QuickBooks status';
  if (state === 'not_configured') return 'QuickBooks is not set up on this server yet — IT needs to add the keys (see Integrations setup).';
  if (state === 'not_connected') return 'QuickBooks is not connected — an admin can connect it in Admin › Integrations.';
  if (!status.bulk_sync_enabled) return 'Sending invoices in bulk is switched off on this server — IT can switch it on (see Integrations setup).';
  if (!pendingCount) return 'Every raised invoice already has a QuickBooks number.';
  return '';
}

export function QuickBooksChip({ status, lastSync }) {
  const state = quickBooksState(status);
  if (state === 'unknown') return null;
  const label = { connected: 'QuickBooks connected', not_connected: 'QuickBooks not connected', not_configured: 'QuickBooks not set up' }[state];
  const tone = { connected: 'green', not_connected: 'amber', not_configured: 'gray' }[state];
  return (
    <span className="hidden flex-col items-end gap-0.5 leading-tight lg:flex" data-testid="quickbooks-status">
      <StatusPill tone={tone} label={label} size="sm" />
      {state === 'connected' ? (
        <span className="text-[11.5px] text-ink-3">{lastSync ? `synced ${formatTimeAgo(lastSync)}` : 'nothing synced yet'}</span>
      ) : null}
    </span>
  );
}

export const SYNC_BATCH = 25;
const rowKey = (kind, id) => `${kind === 'vo' || kind === 'variation' ? 'vo' : 'base'}:${id}`;

/**
 * Sends the pending invoices in batches of SYNC_BATCH and adds the results up. The server also stops starting
 * new invoices after its time budget (so no single request runs long behind a proxy) and returns those as
 * `not_tried`: they go back to the front of the queue for the next request. Invoices another sync is already
 * sending, or that are not raised / already in QuickBooks, come back as `skipped`. Stops when the server says
 * QuickBooks cannot be used right now (`stopped`: expired connection, unreachable, not answering).
 */
export async function syncInBatches(pending, onProgress) {
  const total = { requested: 0, synced_count: 0, synced: [], failed: [], skipped: [], stopped: '' };
  let queue = [...pending];
  let done = 0;
  while (queue.length) {
    const batch = queue.slice(0, SYNC_BATCH);
    const res = await api.post('/quickbooks/sync-pending', {
      invoice_ids: batch.filter((row) => row.kind !== 'vo').map((row) => row.id),
      variation_invoice_ids: batch.filter((row) => row.kind === 'vo').map((row) => row.id),
    });
    const data = res.data || {};
    const notTried = new Set((Array.isArray(data.not_tried) ? data.not_tried : []).map((item) => rowKey(item.kind, item.id)));
    const handled = batch.filter((row) => !notTried.has(rowKey(row.kind, row.id)));
    total.requested += Number(data.requested) || 0;
    total.synced_count += Number(data.synced_count) || 0;
    total.synced.push(...(Array.isArray(data.synced) ? data.synced : []));
    total.failed.push(...(Array.isArray(data.failed) ? data.failed : []));
    total.skipped.push(...(Array.isArray(data.skipped) ? data.skipped : []));
    done += handled.length;
    onProgress?.({ done: Math.min(pending.length, done), total: pending.length });
    if (data.stopped) {
      total.stopped = data.stopped;
      break;
    }
    // The server always handles at least one invoice per request; if it did not, stop rather than loop.
    if (!handled.length) break;
    queue = [...batch.filter((row) => notTried.has(rowKey(row.kind, row.id))), ...queue.slice(batch.length)];
  }
  return total;
}

/**
 * props: status (useQuickBooksStatus), pending (rows without a QuickBooks number), onSynced (reload).
 */
export function SyncToQuickBooksButton({ status, pending, onSynced }) {
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const count = pending.length;
  const blocked = syncBlockedReason(status, count);
  if (quickBooksState(status) === 'unknown') return null;

  const run = async () => {
    let result = null;
    const ok = await confirm({
      title: `Send ${plural(count, 'invoice')} to QuickBooks?`,
      description: 'Each raised invoice without a QuickBooks number is created in QuickBooks under its project’s client. Invoices that fail are listed afterwards; the others are not affected.',
      consequences: [
        `${formatNumber(pending.filter((row) => row.kind === 'base').length)} base invoices`,
        `${formatNumber(pending.filter((row) => row.kind === 'vo').length)} variation invoices`,
      ],
      confirmLabel: `Send ${formatNumber(count)}`,
      icon: RefreshCw,
      onConfirm: async () => {
        setBusy(true);
        try {
          result = await syncInBatches(pending, setProgress);
        } finally {
          setBusy(false);
          setProgress(null);
        }
      },
    });
    if (!ok || !result) return;
    const failed = Array.isArray(result.failed) ? result.failed : [];
    const skipped = Array.isArray(result.skipped) ? result.skipped : [];
    const skippedText = skipped.length ? `${formatNumber(skipped.length)} skipped (${[...new Set(skipped.map((item) => item.reason).filter(Boolean))].join(', ').toLowerCase() || 'not sent'})` : '';
    if (failed.length) {
      notify.warning(`${formatNumber(result.synced_count || 0)} sent · ${formatNumber(failed.length)} failed`, {
        description: [
          result.stopped || failed.slice(0, 3).map((item) => `${item.reference || 'No ref.'}: ${item.error}`).join(' · '),
          skippedText,
        ].filter(Boolean).join(' · '),
      });
    } else if (skipped.length) {
      notify.success(`${plural(result.synced_count || 0, 'invoice')} sent to QuickBooks`, { description: skippedText });
    } else {
      notify.success(`${plural(result.synced_count || 0, 'invoice')} sent to QuickBooks`);
    }
    await onSynced?.();
  };

  return (
    <span title={blocked || undefined} className="inline-flex">
      <Button
        variant="outline"
        onClick={() => run().catch((err) => notify.error('Could not sync to QuickBooks', { description: getErrorMessage(err) }))}
        disabled={Boolean(blocked) || busy}
        className="px-2.5 text-ink sm:px-3.5"
        aria-label={`Sync ${count} to QuickBooks`}
        aria-describedby={blocked ? 'quickbooks-sync-blocked' : undefined}
        data-testid="quickbooks-sync-all"
      >
        <RefreshCw className={busy ? 'animate-spin' : undefined} aria-hidden="true" />
        <span className="hidden sm:inline">
          {progress ? `Syncing ${formatNumber(progress.done)} of ${formatNumber(progress.total)}…` : `Sync ${formatNumber(count)} to QuickBooks`}
        </span>
      </Button>
      {blocked ? <span id="quickbooks-sync-blocked" className="sr-only">{blocked}</span> : null}
    </span>
  );
}
